import React, { useState } from 'react';
import MainLayout from '../components/MainLayout';
import { useQuery } from '@apollo/client';
import { GET_PROFILE } from '../apollo/Queries';
import { UserCircle, Save, ShieldCheck } from 'lucide-react';

export default function ProfilePage() {
  const [selectedUser, setSelectedUser] = useState(null);
  const { data, loading } = useQuery(GET_PROFILE, {
    variables: {
      page: 0,
      size: 10
    }
  });

  const profile = data?.profile;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedUser(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    alert(`Identity updated: ${JSON.stringify(profile?.data, null, 2)}`);
  };

  return (
    <MainLayout page="Account">
      <div className="max-w-4xl mx-auto space-y-8 animate-fade-in-up">
        {/* Profile Card */}
        <div className="bg-white rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.03)] border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-64 gap-6">
              <div className="w-16 h-16 rounded-2xl border-4 border-premium-gold/20 border-t-premium-gold animate-spin"></div>
              <p className="text-[10px] font-black text-premium-gold uppercase tracking-[0.5em] animate-pulse">Decrypting Identity...</p>
            </div>
          ) : profile && profile?.data ? (
            <>
              {/* Header Section */}
              <div className="bg-gradient-to-br from-premium-teal to-premium-dark p-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-premium-gold/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2"></div>
                <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                  <div className="w-24 h-24 rounded-[2rem] bg-premium-gold/10 p-1">
                    <div className="w-full h-full rounded-[1.8rem] bg-premium-teal flex items-center justify-center text-premium-gold border border-white/10 shadow-xl">
                      <UserCircle size={48} />
                    </div>
                  </div>
                  <div className="text-center md:text-left space-y-1">
                    <div className="flex items-center justify-center md:justify-start gap-3">
                      <span className="text-[10px] font-black uppercase tracking-[0.4em] text-premium-gold/80 px-3 py-1 bg-white/5 rounded-full border border-white/5">User Profile</span>
                    </div>
                    <h2 className="text-4xl font-black text-white tracking-tighter">
                      {profile?.data?.firstName} {profile?.data?.lastName}
                    </h2>
                    <p className="text-teal-100/60 font-light italic">System User.</p>
                  </div>
                </div>
              </div>
              <div className="hidden">{selectedUser?.firstName}</div>
              {/* Form Section */}
              <div className="p-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-xs font-black text-premium-gold uppercase tracking-[0.2em] mb-2 px-1">First Name</label>
                      <input
                        name="firstName"
                        defaultValue={profile?.data?.firstName}
                        onChange={handleInputChange}
                        className="block w-full px-5 py-4 bg-gray-50/50 border border-gray-100 rounded-[1.5rem] text-premium-teal font-bold placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-premium-gold/30 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-black text-premium-gold uppercase tracking-[0.2em] mb-2 px-1">Last Name</label>
                      <input
                        name="lastName"
                        defaultValue={profile?.data?.lastName}
                        onChange={handleInputChange}
                        className="block w-full px-5 py-4 bg-gray-50/50 border border-gray-100 rounded-[1.5rem] text-premium-teal font-bold placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-premium-gold/30 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-xs font-black text-premium-gold uppercase tracking-[0.2em] mb-2 px-1">Telephone</label>
                      <input
                        name="phone"
                        defaultValue={profile?.data?.phone}
                        onChange={handleInputChange}
                        className="block w-full px-5 py-4 bg-gray-50/50 border border-gray-100 rounded-[1.5rem] text-premium-teal font-bold placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-premium-gold/30 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-black text-premium-gold uppercase tracking-[0.2em] mb-2 px-1">Username</label>
                      <input
                        name="username"
                        defaultValue={profile?.data?.username}
                        onChange={handleInputChange}
                        className="block w-full px-5 py-4 bg-gray-50/50 border border-gray-100 rounded-[1.5rem] text-premium-teal font-bold placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-premium-gold/30 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-50 flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="flex items-center gap-3 text-premium-teal/40">
                    <ShieldCheck size={20} />
                    <span className="text-[10px] font-black uppercase tracking-widest">User Type: {profile?.data?.userType}</span>
                  </div>

                  <button
                    onClick={handleSave}
                    className="premium-button flex items-center gap-3 bg-premium-gold text-premium-teal px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-premium-gold/20"
                  >
                    <Save size={18} />
                    Confirm Changes
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-400 font-light italic">The identity could not be retrieved from the collective.</p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
